import React, { createContext, useContext, useEffect, useRef, useState } from "react"

import ElementExplosionAnimation from "../liveUserStats/ElementExplosionAnimation"

import Vector2 from "../../utils/baseClasses/Vector2"

// Import types
import type { PropsWithChildren } from "react"
import type { ElementExplosionAnimationProps } from "../liveUserStats/ElementExplosionAnimation"
import ParticleEffectWrapper from "./ParticleEffectWrapper"

// Define types
export interface ParticleEffect {
  id: string

  anchorElement: Element
  positionOffset: Vector2

  params: ElementExplosionAnimationProps
}

// Context
interface ParticleEffectContextType {
  particleEffects: ParticleEffect[]

  addParticleEffect: (
    anchorElement: Element,
    params: ElementExplosionAnimationProps,
    positionOffset?: Vector2,
  ) => void
}
const ParticleEffectContext = createContext<ParticleEffectContextType>({
  particleEffects: [],

  addParticleEffect: () => { }
})

// Provider
export const ParticleEffectProvider: React.FC<PropsWithChildren> = ({
  children,
}) => {
  const [particleEffects, setParticleEffects] = useState<ParticleEffect[]>([])
  const particleEffectsRef = useRef<ParticleEffect[]>([])

  // Add particle effect
  const addParticleEffect = (
    anchorElement: Element,
    params: ElementExplosionAnimationProps,
    positionOffset: Vector2 = new Vector2(50, 50),
  ) => {
    // Create a new particle effect object
    const newParticleEffect: ParticleEffect = {
      id: crypto.randomUUID(),

      anchorElement,
      positionOffset,

      params,
    }

    // Add object to effect array and update state
    particleEffectsRef.current.push(newParticleEffect)
    setParticleEffects([...particleEffectsRef.current])
  }

  // Remove particle effect
  const removeParticleEffect = (id: string) => {
    // Remove object from effect array and update state
    const index = particleEffectsRef.current.findIndex((particleEffect) => particleEffect.id == id)
    particleEffectsRef.current.splice(index, 1)
    setParticleEffects([...particleEffectsRef.current])
  }

  return (
    <ParticleEffectContext.Provider
      value={{
        particleEffects,

        addParticleEffect,
      }}
    >
      {children}

      <div
        style={{
          position: "fixed",
          left: "0",
          top: "0",
          zIndex: "10000",
          pointerEvents: "none",
        }}
      >
        {particleEffects.map((particleEffect) => (
          <ParticleEffectWrapper
            key={particleEffect.id}
            anchorElement={particleEffect.anchorElement}
            positionOffset={particleEffect.positionOffset}
          >
            <ElementExplosionAnimation
              key={particleEffect.id}

              {...particleEffect.params}
              onAnimationFinish={() => removeParticleEffect(particleEffect.id)}
            />
          </ParticleEffectWrapper>
        ))}
      </div>
    </ParticleEffectContext.Provider>
  )
}

// Use context
export const useParticleEffect = () => useContext(ParticleEffectContext)
